/**
 * Form state for the auth flow.
 *
 * The one rule that shapes this composable (Figma `66:47545`): the submit button
 * is gated *only* on what the client can verify by itself. Anything the server
 * alone knows — email already registered, wrong credentials, expired token —
 * never touches `submitDisabled`. It surfaces after submit and clears when the
 * user edits the field that caused it.
 *
 * That is why `clientValid` and `serverError` are two separate channels rather
 * than one merged `errors` bag: merging them is exactly what makes the sign-up
 * button wrongly go dead on "this email is already registered" (`66:47517`).
 */
import { computed, ref, type ComputedRef, type Ref } from 'vue'
import type { AuthError, AuthErrorScope } from './mapAuthError'

export interface UseAuthFormValidationOptions {
  /** Every locally verifiable rule for this form, combined. */
  clientValid: ComputedRef<boolean> | Ref<boolean>
}

export function useAuthFormValidation({ clientValid }: UseAuthFormValidationOptions) {
  const serverError = ref<AuthError | null>(null)
  const isPending = ref(false)

  const submitDisabled = computed(() => !clientValid.value || isPending.value)

  function setServerError(error: AuthError | null) {
    serverError.value = error
  }

  function clearServerError() {
    serverError.value = null
  }

  /**
   * Call from the `input` handler of every field. A `form`-scoped error (the
   * login banner) clears on any edit; a field-scoped one only when that field
   * is touched.
   */
  function notifyFieldEdited(field: AuthErrorScope) {
    const current = serverError.value
    if (!current) return
    if (current.scope === 'form' || current.scope === 'network' || current.scope === field) {
      serverError.value = null
    }
  }

  /** Banner above the fields, with both of them underlined in red. */
  const formError = computed(() =>
    serverError.value?.scope === 'form' ? serverError.value : null,
  )

  function fieldError(field: 'email' | 'password'): AuthError | null {
    return serverError.value?.scope === field ? serverError.value : null
  }

  async function submit<T>(run: () => Promise<T>): Promise<T | undefined> {
    if (submitDisabled.value) return undefined
    isPending.value = true
    try {
      return await run()
    } finally {
      isPending.value = false
    }
  }

  return {
    serverError,
    isPending,
    submitDisabled,
    formError,
    fieldError,
    setServerError,
    clearServerError,
    notifyFieldEdited,
    submit,
  }
}
